import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Code2, Copy, CheckCircle, Loader2, Key } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { mcpKeyService, MCPKey } from '../../../services/mcpKeyService';
import { subdomainApiService } from '../../../services/subdomainApiService';
import { useUpstreamFile } from '../useUpstreamFile';

export const ApiEndpointNode: React.FC<{ id: string; data: { label: string; color: string } }> = ({ id, data }) => {
  const selectedFile = useUpstreamFile(id);
  const [apiKey, setApiKey] = useState<MCPKey | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [subdomain, setSubdomain] = useState<string | undefined>();

  useEffect(() => {
    setLoading(true);
    mcpKeyService
      .getCurrentKey()
      .then((k) => setApiKey(k))
      .catch(() => {})
      .finally(() => setLoading(false));

    subdomainApiService
      .getSubdomainConfig()
      .then((config) => setSubdomain(config?.subdomain))
      .catch(() => {});
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const key = await mcpKeyService.generateKey();
      setApiKey(key);
    } catch {
      // ignore
    } finally {
      setGenerating(false);
    }
  };

  const endpointUrl = subdomain ? subdomainApiService.getApiEndpoint(subdomain) : '';
  const maskedKey = apiKey ? apiKey.api_key.slice(0, 8) + '...' + apiKey.api_key.slice(-4) : '';

  const curlCmd = selectedFile && subdomain
    ? subdomainApiService.generateCurlCommand(
        apiKey ? apiKey.api_key : '<YOUR_API_KEY>',
        selectedFile.fileName,
        'result = df.lazy().head(10).collect()',
        1000,
        subdomain
      )
    : '';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[400px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Code2 className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {!selectedFile ? (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Connect a data source
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
            <span className="truncate">{selectedFile.fileName}</span>
          </div>
        )}

        {selectedFile && (
          <>
            {/* API Key */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                API Key (X-API-Key)
              </p>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : apiKey ? (
                <div className="flex items-center gap-1.5">
                  <Badge variant="secondary" className="text-[10px] font-mono px-1.5">
                    <Key className="h-2.5 w-2.5 mr-1" />
                    {maskedKey}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    onClick={() => copyToClipboard(apiKey.api_key)}
                  >
                    {copied ? (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Key className="h-3 w-3 mr-1" />
                  )}
                  Generate Key
                </Button>
              )}
            </div>

            {/* Endpoint URL */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Endpoint
              </p>
              <div className="flex items-center gap-1">
                <code className="text-[10px] bg-muted rounded px-1.5 py-0.5 truncate flex-1 block">
                  {endpointUrl}/query
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0"
                  onClick={() => copyToClipboard(`${endpointUrl}/query`)}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Curl example */}
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Example
              </p>
              <div className="relative nodrag nowheel">
                <pre className="text-[10px] bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed font-mono max-h-[120px]">
                  {curlCmd}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 absolute top-1 right-1"
                  onClick={() => copyToClipboard(curlCmd)}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
    </div>
  );
};
